import React, {useState, useEffect, useRef, useCallback} from 'react';
import {Button, Dialog} from '@betareader/design-system';
import {useTranslation} from 'react-i18next';

import SceneEditor from './sceneEditor';
import {computeCounts} from './sceneCommentMapping';
import {parseSceneDoc} from './tiptapExtensions';

import {newStableId as newSceneId} from './ids';

function emptyScene() {
  return {
    id: newSceneId(),
    content: '',
    wordCount: 0,
    charCount: 0,
    paragraphCount: 0
  };
}

// A scene keeps a private `text` for computing the chapter `beginning`; it is
// never sent to the server (only id/content/counts are).
function withDerivedText(scene) {
  const counts = computeCounts(parseSceneDoc(scene.content));
  return {...scene, text: counts.text};
}

function initialScenes(chapter) {
  if (chapter && Array.isArray(chapter.scenes) && chapter.scenes.length > 0) {
    return chapter.scenes.map(scene => withDerivedText({...scene}));
  }
  return [withDerivedText(emptyScene())];
}

function toPayload(scenes) {
  const wordCount = scenes.reduce((sum, s) => sum + (s.wordCount || 0), 0);
  const charCount = scenes.reduce((sum, s) => sum + (s.charCount || 0), 0);
  const paragraphCount = scenes.reduce(
    (sum, s) => sum + (s.paragraphCount || 0),
    0
  );
  const beginning = scenes
    .map(s => s.text || '')
    .join(' ')
    .replace(/\s+/g, ' ')
    .trim()
    .substring(0, 50);
  return {
    // Strip the private `text` field before it leaves the component.
    scenes: scenes.map(({text, ...rest}) => rest),
    wordCount,
    charCount,
    paragraphCount,
    beginning
  };
}

/**
 * Renders a chapter of a 'tiptap'-format book as a vertical list of scene
 * editors separated by scene-break dividers, with per-scene controls (add
 * after / delete / move up / move down). Maintains scenes[] with stable ids and
 * calls onChange(payload) with the chapter aggregates whenever content changes,
 * matching the shape the chapter PUT/POST routes expect
 * ({scenes, wordCount, charCount, paragraphCount, beginning}).
 */
const SceneChapterEditor = ({
  chapter,
  editable = true,
  onChange,
  onInitialized
}) => {
  const {t} = useTranslation();
  const [scenes, setScenes] = useState(() => initialScenes(chapter));
  const [pendingDeleteIndex, setPendingDeleteIndex] = useState(null);

  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const initializedRef = useRef(false);

  // Reset when navigating to a different chapter.
  useEffect(() => {
    setScenes(initialScenes(chapter));
    setPendingDeleteIndex(null);
  }, [chapter?._id]);

  useEffect(() => {
    if (!initializedRef.current) {
      initializedRef.current = true;
      onInitialized && onInitialized();
    }
  }, [onInitialized]);

  // Commit a new scenes array and push chapter aggregates to the parent (which
  // drives autosave). Called only from user-driven event handlers, never from
  // a render/updater, so the parent setState is safe.
  const commit = useCallback(nextScenes => {
    setScenes(nextScenes);
    if (onChangeRef.current) onChangeRef.current(toPayload(nextScenes));
  }, []);

  const handleSceneChange = (index, content, counts) => {
    const next = scenes.map((scene, i) =>
      i === index
        ? {
            ...scene,
            content,
            wordCount: counts.wordCount,
            charCount: counts.charCount,
            paragraphCount: counts.paragraphCount,
            text: counts.text
          }
        : scene
    );
    commit(next);
  };

  const addSceneAfter = index => {
    const next = [...scenes];
    next.splice(index + 1, 0, withDerivedText(emptyScene()));
    commit(next);
  };

  const moveScene = (index, delta) => {
    const target = index + delta;
    if (target < 0 || target >= scenes.length) return;
    const next = [...scenes];
    const [moved] = next.splice(index, 1);
    next.splice(target, 0, moved);
    commit(next);
  };

  const confirmDelete = () => {
    const index = pendingDeleteIndex;
    setPendingDeleteIndex(null);
    if (scenes.length <= 1) return; // never delete the last scene
    commit(scenes.filter((_, i) => i !== index));
  };

  return (
    <div className="scene-chapter-editor">
      {scenes.map((scene, index) => (
        <div key={scene.id} className="scene-block">
          {index > 0 && (
            <div className="scene-break" aria-hidden>
              <span>⁂</span>
            </div>
          )}

          <SceneEditor
            scene={scene}
            editable={editable}
            onChange={(content, counts) =>
              handleSceneChange(index, content, counts)
            }
          />

          {editable && (
            <div className="scene-controls">
              <div style={{display: 'flex', flexWrap: 'wrap', gap: 'var(--br-space-1)'}}>
                <Button
                  variant='secondary'
                  size='small'
                  disabled={index === 0}
                  title={t('MoveUp')}
                  aria-label={t('MoveUp')}
                  onClick={() => moveScene(index, -1)}
                >↑</Button>
                <Button
                  variant='secondary'
                  size='small'
                  disabled={index === scenes.length - 1}
                  title={t('MoveDown')}
                  aria-label={t('MoveDown')}
                  onClick={() => moveScene(index, 1)}
                >↓</Button>
                <Button
                  variant='secondary'
                  size='small'
                  title={t('AddScene')}
                  aria-label={t('AddScene')}
                  onClick={() => addSceneAfter(index)}
                >+</Button>
                <Button
                  variant='danger'
                  size='small'
                  disabled={scenes.length <= 1}
                  title={t('DeleteScene')}
                  aria-label={t('DeleteScene')}
                  onClick={() => setPendingDeleteIndex(index)}
                >×</Button>
              </div>
            </div>
          )}
        </div>
      ))}

      <Dialog
        open={pendingDeleteIndex !== null}
        title={t('DeleteScene')}
        onClose={() => setPendingDeleteIndex(null)}
        actions={<>
          <Button variant='secondary' onClick={() => setPendingDeleteIndex(null)}>{t('Cancel')}</Button>
          <Button variant='danger' onClick={confirmDelete}>{t('Delete')}</Button>
        </>}
      ><p>{t('DeleteSceneConfirm')}</p></Dialog>
    </div>
  );
};

export default SceneChapterEditor;
